import ItemDropdownSelect from '@src/components/common/ItemDropdownSelect';
import GlobeIcon from '@src/svgs/GlobeIcon';
import { normalizeLangKey } from '@src/utility/bcp47';
import { type ReactNode } from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import type { LanguageRef } from '@src/repositories/TranslationMemoryEntryRepository';

const MENU_CLASS = 'tm-language-menu';
// Sentinel value for the "All" row (empty string = no language filter).
const ALL_VALUE = '';

interface Props {
  /** Selected language id, or '' for "All". */
  value: string;
  onChange: (value: string) => void;
  languages: LanguageRef[];
  /** Show an "All" row (table filters); omit for the matrix base language,
   *  which must resolve to a concrete language. */
  allLabel?: string;
  ariaLabel: string;
  /** Distinguishes the two dropdowns' `action` callbacks. */
  propertyName: string;
}

/**
 * Compact language picker for the Translation Memory filter bar, matching the
 * Transcript & Speakers language dropdown (globe + code trigger; a menu of full
 * language names with the BCP-47 code right-aligned). Reuses the shared
 * `ItemDropdownSelect`; the option renders a chip in the trigger and a two-column
 * row inside the menu, toggled by the scoped menu-class CSS.
 */
const LanguageDropdown = ({
  value,
  onChange,
  languages,
  allLabel,
  ariaLabel,
  propertyName,
}: Props) => {
  const values: { caption: ReactNode; value: string }[] = [
    ...(allLabel
      ? [
          {
            value: ALL_VALUE,
            caption: (
              <OptionWrapper data-option-wrapper="">
                <OptionChip data-option-chip="">
                  <GlobeIconWrap aria-hidden="true">
                    <GlobeIcon width="14" height="14" />
                  </GlobeIconWrap>
                  All
                </OptionChip>
                <OptionRow data-option-row="">
                  <OptionLabel>{allLabel}</OptionLabel>
                </OptionRow>
              </OptionWrapper>
            ),
          },
        ]
      : []),
    ...languages.map(l => {
      // Canonical BCP 47 for display, as the transcript menu (PLAT-11652).
      const code = normalizeLangKey(l.value ?? l.id);
      const label = l.caption ?? l.value ?? l.id;
      const selected = l.id === value;
      return {
        value: l.id,
        caption: (
          <OptionWrapper data-option-wrapper="">
            <OptionChip data-option-chip="">
              <GlobeIconWrap aria-hidden="true">
                <GlobeIcon width="14" height="14" />
              </GlobeIconWrap>
              <OptionCode>{code}</OptionCode>
            </OptionChip>
            <OptionRow data-option-row="">
              <OptionLabel $selected={selected}>{label}</OptionLabel>
              <OptionRowCode $selected={selected}>{code}</OptionRowCode>
            </OptionRow>
          </OptionWrapper>
        ),
      };
    }),
  ];

  return (
    <Wrapper aria-label={ariaLabel}>
      <MenuGlobalStyle />
      <ItemDropdownSelect
        propertyName={propertyName}
        menuClassName={MENU_CLASS}
        defaultValue={value}
        values={values}
        action={(_, v) => onChange(v ?? ALL_VALUE)}
      />
    </Wrapper>
  );
};

export default LanguageDropdown;

// Trigger sizing: match the filter bar's inputs (compact, bordered field) and
// keep the trigger narrow — it only shows the globe + code chip.
const Wrapper = styled.div`
  .Selected {
    height: var(--btn-h-sm);
    padding: 0 10px;
    border-radius: var(--radius-input);
    gap: 6px;
  }
`;

// Menu rows show the two-column row (full name left, code right); the trigger
// collapses to the chip. ItemDropdownSelect portals the menu to <body> and
// sizes it to the (compact) trigger width, so widen it to fit the longest name.
const MenuGlobalStyle = createGlobalStyle`
  .${MENU_CLASS} {
    width: max-content !important;
    min-width: 220px !important;
    max-width: 90vw !important;
    white-space: nowrap;
  }
  .${MENU_CLASS} [data-option-chip] {
    display: none;
  }
  .${MENU_CLASS} [data-option-row] {
    display: grid;
  }
  .${MENU_CLASS} [data-option-wrapper] {
    display: flex;
    width: 100%;
  }
`;

const OptionWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  font-variant-numeric: tabular-nums;
`;

const OptionChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const OptionRow = styled.span`
  display: none;
  width: 100%;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 24px;
`;

const GlobeIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  color: currentColor;
  opacity: 0.9;
`;

const OptionCode = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.02em;
  text-transform: lowercase;
`;

const OptionLabel = styled.span<{ $selected?: boolean }>`
  font-size: 11px;
  color: ${p => (p.$selected ? 'var(--accent)' : 'var(--foreground)')};
  font-weight: ${p =>
    p.$selected ? 'var(--font-weight-semibold)' : 'inherit'};
  white-space: nowrap;
`;

const OptionRowCode = styled.span<{ $selected?: boolean }>`
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.02em;
  color: ${p => (p.$selected ? 'var(--accent)' : 'var(--text-muted)')};
  font-variant-numeric: tabular-nums;
  text-transform: lowercase;
  justify-self: end;
`;
